import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const CardList = () => {
  const [cards, setCards] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [hoverTarget, setHoverTarget] = useState(null);
  
  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Convert cards structure to diagram data format and return as a JSON string
  const getDiagramDataFromCardList = () => {
    // Helper function to convert a card and its children to the new format
    const convertCardToNode = (card) => {
      return {
        name: card.content,
        nodes: card.children.map(child => convertCardToNode(child))
      };
    };
    
    // Start with the root level cards
    const diagramData = {
      nodes: cards.map(card => convertCardToNode(card))
    };
    
    // Return as a JSON string
    return JSON.stringify(diagramData);
  };

  // Expose the method to the window object so it can be called from outside React
  useEffect(() => {
    // Add the method to the window object
    window.getDiagramDataFromCardList = getDiagramDataFromCardList;
    
    // Clean up when component unmounts
    return () => {
      delete window.getDiagramDataFromCardList;
    };
  }, [cards]); // Re-register when cards change to ensure it uses the latest data

  // Add a new card
  const handleAddCard = () => {
    if (!inputValue.trim()) return;
    
    // Check if the input value already exists in the cards
    const isDuplicate = checkForDuplicate(inputValue, cards);
    if (isDuplicate) {
      alert('A card with this name already exists. Please use a unique name.');
      return;
    }

    const newCard = {
      id: `card-${Date.now()}`,
      content: inputValue,
      children: [],
      parent: null
    };
    
    setCards([...cards, newCard]);
    setInputValue('');
  };

  // Helper function to check for duplicate card names
  const checkForDuplicate = (text, cardList) => {
    for (const card of cardList) {
      if (card.content === text) return true;
      if (card.children.length > 0) {
        const foundInChildren = checkForDuplicate(text, card.children);
        if (foundInChildren) return true;
      }
    }
    return false;
  };

  // Find a card by ID (recursively searching through children)
  const findCardById = (id, cardList) => {
    for (const card of cardList) {
      if (card.id === id) return card;
      
      if (card.children.length > 0) {
        const found = findCardById(id, card.children);
        if (found) return found;
      }
    }
    return null;
  };
  
  // Remove a card from its current position (recursively)
  const removeCardFromList = (id, cardList) => {
    // Check top level first
    const topLevelIndex = cardList.findIndex(card => card.id === id);
    if (topLevelIndex !== -1) {
      return cardList.filter(card => card.id !== id);
    }
    
    // Check children recursively
    return cardList.map(card => {
      if (card.children.length > 0) {
        return {
          ...card,
          children: removeCardFromList(id, card.children)
        };
      }
      return card;
    });
  };
  
  // Add a card to a parent
  const addCardToParent = (card, parentId, cardList) => {
    return cardList.map(item => {
      if (item.id === parentId) {
        return {
          ...item,
          children: [...item.children, { ...card, parent: parentId }]
        };
      }
      
      if (item.children.length > 0) {
        return {
          ...item,
          children: addCardToParent(card, parentId, item.children)
        };
      }
      
      return item;
    });
  };

  // Delete a card and its children
  const handleDeleteCard = (id) => {
    // Remove the card from the list (and any parent's children list)
    const updatedCards = removeCardAndChildren(id, cards);
    setCards(updatedCards);
  };
  
  // Function to remove a card and its children from the list
  const removeCardAndChildren = (id, cardList) => {
    // First check if it's a top-level card
    const topLevelIndex = cardList.findIndex(card => card.id === id);
    if (topLevelIndex !== -1) {
      return cardList.filter(card => card.id !== id);
    }
    
    // Otherwise look through children recursively
    return cardList.map(card => {
      if (card.children.length > 0) {
        return {
          ...card,
          children: removeCardAndChildren(id, card.children)
        };
      }
      return card;
    });
  };

  // Add function to move a card to the root level
  const moveCardToRoot = (id) => {
    // Find the card
    const card = findCardById(id, cards);
    if (!card) return;
    
    // Skip if already at root level
    if (!card.parent) return;
    
    // Remove card from its current position
    const newCards = removeCardFromList(id, cards);
    
    // Add to root level without a parent
    const cardForRoot = {...card, parent: null};
    
    setCards([...newCards, cardForRoot]);
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
  };

  // Add new function to handle hover events during drag
  const handleDragOver = (event) => {
    const { active, over } = event;
    
    // Return if no over target
    if (!over) {
      setHoverTarget(null);
      return;
    }
    
    // Don't show hover effect on the dragged item itself
    if (active.id === over.id) {
      setHoverTarget(null);
      return;
    }
    
    // Track the item being hovered over for linking visual indication
    setHoverTarget(over.id);
  };

  // Handle drag end with updated hover state cleanup
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    // Clear hover state
    setHoverTarget(null);
    
    if (!over) return;
    
    const activeId = active.id;
    const overId = over.id;
    
    if (activeId === overId) return;
    
    const activeCard = findCardById(activeId, cards);
    if (!activeCard) return;
    
    // If card is dropped on the root container area, move it to root level
    if (overId === 'root-container') {
      if (activeCard.parent) {
        moveCardToRoot(activeId);
      }
      return;
    }
    
    // Determine if we're dropping onto a card or just reordering
    if (overId.toString().startsWith('card-')) {
      // We're dropping onto another card - create parent-child relationship
      const overCard = findCardById(overId, cards);
      if (!overCard) return;
      
      // Prevent circular references
      let currentParent = overCard.parent;
      while (currentParent) {
        if (currentParent === activeId) {
          // Can't drop onto a descendant
          return;
        }
        const parent = findCardById(currentParent, cards);
        currentParent = parent?.parent;
      }
      
      // Remove card from its original position
      const newCards = removeCardFromList(activeId, cards);
      
      // Add to new parent
      const updatedCards = addCardToParent(activeCard, overId, newCards);
      
      setCards(updatedCards);
    } else {
      // Reordering within the same list
      const activeIndex = cards.findIndex(card => card.id === activeId);
      const overIndex = cards.findIndex(card => card.id === overId);
      
      if (activeIndex !== -1 && overIndex !== -1) {
        setCards(arrayMove(cards, activeIndex, overIndex));
      }
    }
    
    setActiveId(null);
  };

  // Sortable card component with improved hover behavior
  const SortableCard = ({ card }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ 
      id: card.id,
      // Disable automatic animation for other cards when dragging
      transition: {
        duration: isDragging ? 0 : 250,
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)'
      }
    });

    // Check if this card is being hovered over for linking
    const isHovered = hoverTarget === card.id;

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.4 : 1,
      position: 'relative',
      zIndex: isDragging ? 10 : 1,
      // Add a subtle highlight for hover target
      boxShadow: isHovered ? '0 0 0 2pxrgb(167, 167, 167)' : 'none'
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`card p-3 mb-3 bg-white border rounded shadow-sm 
          ${isDragging ? 'ring-2 ring-gray-500' : ''} 
          ${isHovered ? 'bg-gray-50' : ''}`}
      >
        {/* Card header with content and delete button */}
        <div className="flex justify-between items-center">
          <div 
            className="card-content font-medium cursor-move flex-grow"
            {...attributes}
            {...listeners}
          >
            {card.content}
          </div>
          <div className="flex">
            {/* Add unlink button if card is nested */}
            {card.parent && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  moveCardToRoot(card.id);
                }}
                className="delete-button mr-2 text-gray-400 hover:text-gray-500 focus:outline-none"
                title="Move to root level"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </button>
            )}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteCard(card.id);
              }}
              className="delete-button text-gray-400 hover:text-red-500 focus:outline-none"
              title="Delete card"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Visual indicator for linking */}
        {isHovered && !isDragging && activeId && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-gray-100 border border-gray-300 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">
              Drop to nest under this card
            </div>
          </div>
        )}
        
        {/* Child cards */}
        {card.children.length > 0 && (
          <div className="card-children pl-4 mt-2 border-l-2 border-gray-500">
            <SortableContext items={card.children.map(child => child.id)} strategy={verticalListSortingStrategy}>
              {card.children.map(child => (
                <SortableCard key={child.id} card={child} />
              ))}
            </SortableContext>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card-manager">
      <div className="input-container flex mb-4">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddCard()}
          placeholder="Enter card content..."
          className="flex-grow px-4 py-2 border rounded-l-md focus:outline-none focus:ring-2 focus:ring-gray-500"
        />
        <button 
          onClick={handleAddCard}
          className="px-4 py-2 rounded-r-md focus:outline-none hover:underline"
        >
          Add Card
        </button>
      </div>
      
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div 
          id="root-container"
          className="cards-container p-4 border rounded-md min-h-[300px] bg-gray-50"
        >
          <SortableContext items={cards.map(card => card.id)} strategy={verticalListSortingStrategy}>
            {cards.map((card) => (
              <SortableCard key={card.id} card={card} />
            ))}
          </SortableContext>
          
          {cards.length === 0 && (
            <div className="text-center py-10 text-gray-500">
              No cards yet. Add your first card above!
            </div>
          )}
        </div>
        
        <DragOverlay>
          {activeId ? (
            <div className="card p-3 bg-white border rounded shadow-md">
              {findCardById(activeId, cards)?.content || ''}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
  
};

export default CardList;